import {
    AfterViewInit,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {MvsWidgetConfigFormFieldUi} from "../mvs-widget-configuration/data/mvs-widget-config-form-field.ui";
import {WidgetConfigService} from "../../../service/widget-config.service";
import {WidgetConfigComponentDirective} from "../widget-config-component.directive";
import {Subscription} from "rxjs";
import {WidgetParamConfigRuntime} from "../widget-param-config-runtime";
import {WidgetConfigBaseComponent} from "../widget-config-base.component";
import {WidgetConfigForms} from "../../../service/data/widget-config-forms";
import {
    InputOutputConfigurationOptionsDropdownInterface
} from "../mvs-widget-configuration/input-output-configuration-options.dropdown.interface";
import {WidgetData} from "../../../widget-data";
import {WidgetDataParam} from "../../../widget-data-param";
import {MvsFormGroup} from "../../../../logic/form/mvs-form-group";
import {MvsMessageService} from "../../../../service/message/mvs-message.service";
import {UiMessage} from "../../../../p-messages/p-messages.component";

@Component({
    selector: 'mvs-widget-config',
    templateUrl: './mvs-widget-config.component.html',
    standalone: false
})
export class MvsWidgetConfigComponent
      implements OnInit, OnChanges, AfterViewInit {

  @Input() widgetType: string;
  @Input() widgetDataParams: WidgetDataParam[] = [];

  @Input() outputFields: InputOutputConfigurationOptionsDropdownInterface[];
  @Input() widgetConfigFields: MvsWidgetConfigFormFieldUi[];

  /**
   * The widget data is required to extract information like paging.
   */
  @Input() widgetData?: WidgetData;

  @Output() widgetDataParamsChange = new EventEmitter<WidgetDataParam[]>();

  @ViewChildren(WidgetConfigComponentDirective) dynamicWidgetComponents: QueryList<WidgetConfigComponentDirective>;

  childComponentInstances: ComponentRef<WidgetConfigBaseComponent>[];

  widgetConfigForms: WidgetConfigForms;
  widgetConfigRuntime: WidgetParamConfigRuntime;
  formGroup: MvsFormGroup;

  busy: boolean;  // indicator whether the component is busy
  initialized: boolean; // indicator whether the component was initialized

  formGroupSubscription: Subscription;
  formGroupSubscriptionStateChange: Subscription;

  validationMessages: UiMessage[] = [];

  constructor(
      protected widgetConfigService: WidgetConfigService,
      protected messageService: MvsMessageService) {
  }

  ngOnInit(): void {
    this.initComponent();
    this.refreshComponent();
  }

  /**
   * Initialize Component.
   */
  initComponent() {
  }

  /**
   * Refresh Component.
   */
  refreshComponent() {

    // retrieve configuration form
    this.widgetConfigForms = this.widgetConfigService.getConfigurationForms(this.widgetType);

    // convert the widget parameters to an object
    const widgetParamConfigRuntime = this.refreshRuntimeInformation();

    // create a form group based on the widget config form
    this.formGroup = this.widgetConfigService.createFormGroup(this.widgetConfigForms, widgetParamConfigRuntime);

    // subscribe
    this.unsubscribeFormGroup();


    this.formGroupSubscriptionStateChange = this.formGroup.statusChanges.subscribe(status => {
      this.validationMessages = [];

      if (status == "INVALID"){
        this.validationMessages.push({ severity: 'danger', detail: "Mindestens eine Eingabe ist fehlerhaft" });
      }

    });

    this.formGroupSubscription = this.formGroup.valueChanges.subscribe(value => {

      // refresh runtime
      const runtime = this.refreshRuntimeInformation();

      // validate forms
      const widgetConfigFormValidationResult = this.widgetConfigService.validateForms(this.widgetConfigForms, runtime);

      this.validationMessages = [];

      if (widgetConfigFormValidationResult.hasErrors()) {

        for (let validationError of widgetConfigFormValidationResult.errorMessages) {
          const validationErrorForm = {};
          validationErrorForm[validationError.key] = validationError.message;
          this.formGroup.controls[validationError.field].setErrors(validationErrorForm);
        }

        return;
      } else if (this.formGroup.status == "INVALID") {
        return;
      }

      const storableWidgetParams = this.widgetConfigRuntime.toStorableWidgetParams();

      this.widgetDataParamsChange.emit(storableWidgetParams);

    });


    this.initialized = true;
  }

  /**
   * Refresh runtime information and provide this information to the children as well.
   */
  refreshRuntimeInformation(): WidgetParamConfigRuntime {

    // recalculate the runtime data
    this.widgetConfigRuntime = this.widgetConfigService.mixWidgetConfigurationToRuntime(this.widgetDataParams, this.widgetData, this.formGroup, this.widgetConfigForms);

    // adjust runtime information by the providers (if needed)
    this.widgetConfigService.adjustFormRuntimes(this.widgetConfigForms, this.widgetConfigRuntime);

    // inform child components that the runtime information was changed
    if (this.childComponentInstances) {
      for (let childComponentInstance of this.childComponentInstances) {
        childComponentInstance.instance.widgetConfigRuntime = this.widgetConfigRuntime;
      }
    }

    return this.widgetConfigRuntime;

  }




  ngAfterViewInit() {
    this.childComponentInstances = [];

    this.widgetConfigForms.forms.forEach((widgetConfigForm, index) => {
      const viewContainerRef = this.dynamicWidgetComponents.toArray()[index].viewContainerRef;
      viewContainerRef.clear();
      const componentInstance = viewContainerRef.createComponent(widgetConfigForm.component);
      componentInstance.instance.formGroup = this.formGroup;
      componentInstance.instance.widgetConfigForm = widgetConfigForm;
      componentInstance.instance.widgetConfigRuntime = this.widgetConfigRuntime;
      componentInstance.instance.widgetConfigFields = this.widgetConfigFields;

      this.childComponentInstances.push(componentInstance);
    });
  }

  /**
   * Process changes within Binding.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (!this.initialized) {
      return;
    }

    if (changes["widgetType"]) {
      throw new Error("not supported at the moment!");
    }
  }

  /**
   * Destroy component.
   */
  ngOnDestroy(): void {
    this.unsubscribeFormGroup();
  }

  unsubscribeFormGroup() {
    if (this.formGroupSubscription) {
      this.formGroupSubscription.unsubscribe();
    }

    if (this.formGroupSubscriptionStateChange){
      this.formGroupSubscriptionStateChange.unsubscribe();
    }

  }


}
